// VX-4834: reload-durable storage for the ladder's consecutive-failed-recreate count.
//
// The count used to live only in a `useRef` inside useEscapeHatchCoordination, scoped to one page's
// React tree. But every worklist case-open is a hard full-page navigation, and reaching
// BUMP_AFTER_FAILED_RECREATES takes minutes of sustained ping/pong failure — so in production the
// evidence was wiped on every case-open, long before the ladder could ever cast an exhausted vote.
// EXP-004 confirmed this empirically: 5/5 runs, up to 79 consecutive cases under a maximal wedge
// (activePorts frozen at 1, framesLoaded flatlined), zero organic bumps.
//
// Only this tab's OWN count lives here. The derived vote (`exhausted`) and peer presence stay in the
// BroadcastChannel-synced ObserverRegistry, which is deliberately never persisted — a reloaded tab
// must not rehydrate peers' votes it can no longer verify are live. sessionStorage is per-tab and
// survives reload but not tab close, which is exactly the lifetime this evidence should have.

// Keyed by TAB_ID (see coordination.ts) even though sessionStorage is already tab-scoped, so the
// count can never be read under a different observer identity than the one casting the vote it
// feeds — the counter and the vote must always agree on who they belong to.
const storageKey = (tabId: string): string => `viewer.escapeHatch.failedRecreates.${tabId}`;

// How long a recorded failure stays valid as evidence. Must comfortably exceed the slowest interval
// between two consecutive failures — COLD_START_STALL_MS (180s, PixelWorkerConnection.ts) plus the
// page reload itself — or the count would expire mid-incident and reproduce the bug this store
// fixes. Kept well short of a plausible dormancy so a wedge that quietly resolved cannot leave a
// count sitting around to combine with one unrelated failure later. Applied as a SLIDING window off
// the last write, not an absolute one off the first.
export const FAILED_RECREATE_EVIDENCE_TTL_MS = 6 * 60_000;

interface FailedRecreateEvidence {
  count: number;
  // Generation the failures were observed against; a bump starts a new lineage and voids them.
  generation: number;
  // Epoch ms of the most recent recorded failure — the TTL window's origin.
  updatedAt: number;
}

const parse = (raw: string): FailedRecreateEvidence | null => {
  const value = JSON.parse(raw) as Partial<FailedRecreateEvidence> | null;
  if (
    value == null ||
    typeof value.count !== 'number' ||
    typeof value.generation !== 'number' ||
    typeof value.updatedAt !== 'number'
  ) {
    return null;
  }
  return value as FailedRecreateEvidence;
};

export function readFailedRecreates({
  tabId,
  generation,
  now,
}: {
  tabId: string;
  generation: number;
  now: number;
}): number {
  try {
    const raw = sessionStorage.getItem(storageKey(tabId));
    if (raw == null) return 0;
    const evidence = parse(raw);
    if (evidence == null) return 0;
    if (evidence.generation !== generation) return 0;
    // A negative age means the clock moved backwards since the write (NTP correction, VM resume, a
    // user changing the clock), leaving updatedAt in the future. Treating that as "fresh" would keep
    // the evidence alive for the length of the skew on top of the TTL, so an unrelated later failure
    // could inherit it. An unverifiable timestamp is not evidence: discard it, consistent with this
    // ladder's rule that every default errs toward NOT bumping.
    const ageMs = now - evidence.updatedAt;
    if (ageMs < 0 || ageMs > FAILED_RECREATE_EVIDENCE_TTL_MS) return 0;
    return evidence.count;
  } catch {
    // Malformed payload, or sessionStorage unavailable (privacy mode / disabled storage) — with no
    // trustworthy evidence the conservative answer is "no failures observed", which only ever
    // delays a bump rather than triggering a spurious one.
    return 0;
  }
}

export function writeFailedRecreates({
  tabId,
  generation,
  count,
  now,
}: {
  tabId: string;
  generation: number;
  count: number;
  now: number;
}): void {
  try {
    const evidence: FailedRecreateEvidence = { count, generation, updatedAt: now };
    sessionStorage.setItem(storageKey(tabId), JSON.stringify(evidence));
  } catch {
    // ignore write failures — the in-memory count still drives the ladder within this page
  }
}

export function clearFailedRecreates({ tabId }: { tabId: string }): void {
  try {
    sessionStorage.removeItem(storageKey(tabId));
  } catch {
    // ignore
  }
}
